import React, { useState, useCallback } from 'react';
import { clinicService } from '../../../services/api';

const isEmptySchedule = (s) => {
  if (!s?.schedule) return true;
  return Object.keys(s.schedule).every((k) => !(s.schedule[k]?.length));
};

const normDate = (d) => (d ? String(d).slice(0, 10) : null);

const findMatchingSchedule = (schedules, form) => {
  if (!schedules?.length) return null;
  const wantDefault = Boolean(form?.is_default_schedule);
  const wantFrom = normDate(form?.from_date);
  const wantTo = normDate(form?.to_date);
  return schedules.find((s) => {
    const isDefault = Boolean(s.is_default_schedule);
    if (wantDefault !== isDefault) return false;
    if (wantDefault) return true;
    return normDate(s.from_date) === wantFrom && normDate(s.to_date) === wantTo;
  }) || null;
};

const Step5_Confirm = ({ clinicId, scheduleForm, selectedDoctorIds, doctors, originalByDoctor, onBack, onComplete, error, setError }) => {
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);

  const isDeleteMode = isEmptySchedule(scheduleForm);
  const scheduleData = {
    from_date: scheduleForm.from_date,
    to_date: scheduleForm.to_date,
    is_default_schedule: scheduleForm.is_default_schedule,
    schedule: scheduleForm.schedule
  };

  const handleConfirm = useCallback(async () => {
    setApplying(true);
    setError('');
    setResults(null);
    const success = [];
    const failed = [];
    for (const doctorId of selectedDoctorIds) {
      const doctor = doctors.find((d) => d.id === doctorId);
      try {
        if (isDeleteMode) {
          const originals = originalByDoctor[doctorId] || [];
          const match = findMatchingSchedule(originals, scheduleForm);
          if (match?.id) {
            await clinicService.deleteDoctorSchedule(clinicId, doctorId, match.id);
            success.push(doctor?.primary_name || `Doctor ${doctorId}`);
          } else {
            failed.push({
              name: doctor?.primary_name || `Doctor ${doctorId}`,
              error: 'No matching schedule to delete'
            });
          }
        } else {
          await clinicService.createDoctorSchedule(clinicId, doctorId, scheduleData);
          success.push(doctor?.primary_name || `Doctor ${doctorId}`);
        }
      } catch (err) {
        failed.push({
          name: doctor?.primary_name || `Doctor ${doctorId}`,
          error: err.response?.data?.detail || err.message || 'Failed'
        });
      }
    }
    setResults({ success, failed });
    setApplying(false);
    onComplete?.({
      successCount: success.length,
      failedCount: failed.length,
      success,
      failed,
      clinicId,
      isDeleteMode
    });
  }, [clinicId, scheduleForm, selectedDoctorIds, doctors, originalByDoctor, isDeleteMode, onComplete, setError]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 5: Confirm</h2>
        <p className="text-gray-600">
          {isDeleteMode
            ? `Delete matching schedule for ${doctors.length} doctor${doctors.length !== 1 ? 's' : ''}?`
            : `Add this schedule to ${doctors.length} doctor${doctors.length !== 1 ? 's' : ''}?`}
        </p>
      </div>

      <div className={`rounded-lg p-4 ${isDeleteMode ? 'bg-amber-50 border border-amber-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <p className={`text-sm ${isDeleteMode ? 'text-amber-800' : 'text-yellow-800'}`}>
          {isDeleteMode
            ? 'Empty schedule = delete the matching schedule (same type and dates) for each selected doctor.'
            : 'This will create a new schedule for each selected doctor. Existing schedules will remain unchanged.'}
        </p>
      </div>

      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
        {doctors.map((d) => (
          <div key={d.id} className="p-3 flex items-center">
            <span className="font-medium text-gray-900">{d.primary_name}</span>
            {d.speciality && (
              <span className="text-gray-500 text-sm ml-2">- {d.speciality}</span>
            )}
          </div>
        ))}
      </div>

      {results && (
        <div className="space-y-2">
          {results.success.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-medium text-green-800">
                {isDeleteMode ? 'Deleted: ' : 'Added: '}{results.success.join(', ')}
              </p>
            </div>
          )}
          {results.failed.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium text-red-800">Failed:</p>
              <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                {results.failed.map((f, i) => (
                  <li key={i}>{f.name}: {f.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={applying}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={applying}
          className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {applying ? 'Applying...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
};

export default Step5_Confirm;
